import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Badge, {atomBadgeDesigns, atomBadgeSizes, atomBadgeTypes} from '@s-ui/react-atom-badge'
import Context from '@s-ui/react-context'

import CardAdPriceIndicator from './CardAdPriceIndicator/index.js'

// eslint-disable-next-line react/prop-types
const _getCommitmentText = ({totalMonths}) => {
  const {i18n} = useContext(Context)

  if (typeof totalMonths === 'undefined') return null

  return totalMonths === 0 ? i18n.t('COMMON.COMMITMENT_NONE') : i18n.t('COMMON_N_MONTHS', totalMonths)
}

// eslint-disable-next-line react/prop-types
const _getKmsIncludedText = ({isMobile, kmIncluded = 0, kmIncludedPeriod}) => {
  const {i18n} = useContext(Context)

  if (kmIncluded === 0) return null

  switch (kmIncludedPeriod) {
    case 'MONTHLY':
      return ` · ${i18n.n(kmIncluded)} ${i18n.t('COMMON.UNIT_KMS_MONTH')} ${isMobile ? ' · ' : ''} `
    case 'YEARLY':
      return ` · ${i18n.n(kmIncluded)} ${i18n.t('COMMON.UNIT_KMS_YEAR')} ${isMobile ? ' · ' : ''} `
    case 'TOTAL':
      return ` · ${i18n.n(kmIncluded)} ${isMobile ? ' · ' : ''} `
  }
}

const _getVerticalClass = ({className, isVertical, isFullwidth}) => {
  return cx(className, {
    [`${className}--vertical`]: isVertical,
    [`${className}--fullwidth`]: isFullwidth
  })
}

export const CardAdPriceSubscription = ({
  vertical,
  isExtended,
  isMobile = true,
  isPreview,
  hasBothTargetBuyerOffers,
  hasFinancedPrice,
  hasFootNote,
  monthlySubscriptionFee,
  priceRankIndicator,
  subscriptionPriceOption,
  subscriptionPriceOptions = [],
  targetBuyer
}) => {
  const hasPriceIndicator = Boolean(priceRankIndicator)
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {TARGET_BUYER_IDS} = config.get('subscription')

  const [{kmIncluded, kmIncludedPeriod, monthlyPrice = monthlySubscriptionFee, totalMonths} = {}] =
    typeof subscriptionPriceOption === 'undefined'
      ? subscriptionPriceOptions.filter(({monthlyPrice}) => monthlyPrice === monthlySubscriptionFee)
      : [subscriptionPriceOption]

  const textFootNote = hasFootNote ? '*' : ''
  const isBusinessTargetBuyer = targetBuyer === TARGET_BUYER_IDS.BUSINESS
  const showPriceIndicator = hasPriceIndicator && !isBusinessTargetBuyer

  const subscriptionLabel = isExtended ? i18n.t('COMMON.FEE') : i18n.t('COMMON.FEE_FROM')

  const cardAdPriceSubscriptionLabelClass = cx('mt-CardAdPrice-subscriptionLabel', {
    'mt-CardAdPrice-subscriptionLabel--highlight': !isBusinessTargetBuyer
  })

  const targetBuyerLabel = isBusinessTargetBuyer ? i18n.t('COMMON.RENTING_BUSINESS') : i18n.t('COMMON.RENTING_PRIVATE')
  const showBadge = isExtended && !hasBothTargetBuyerOffers

  return (
    <div
      className={_getVerticalClass({
        className: 'mt-CardAdPrice-subscription',
        isVertical: vertical && isExtended,
        isFullwidth: !hasFinancedPrice
      })}
    >
      {showBadge && (
        <span className="mt-CardAdPrice-subscriptionTargetBuyer">
          <Badge
            design={atomBadgeDesigns.SOFT}
            label={targetBuyerLabel}
            size={atomBadgeSizes.MEDIUM}
            type={atomBadgeTypes.PRIMARY}
          />
        </span>
      )}
      {showPriceIndicator ? (
        <div className="mt-CardAdPrice-priceRank">
          <CardAdPriceIndicator rank={priceRankIndicator} withIcon={false} />
        </div>
      ) : (
        <span className="mt-CardAdPrice-subscriptionLabel">{subscriptionLabel}</span>
      )}
      <p className="mt-CardAdPrice-subscriptionAmount">
        {i18n.c(monthlyPrice)}
        <span className="mt-CardAdPrice-subscriptionAmountPerMonth">{`/${i18n
          .t('COMMON.PLURALS.MONTH', 1)
          .toLowerCase()}${textFootNote}`}</span>
      </p>
      {isPreview ? null : (
        <>
          <div className="mt-CardAdPrice-subscriptionLabel">
            {_getCommitmentText({totalMonths})}
            {_getKmsIncludedText({isMobile, kmIncluded, kmIncludedPeriod})}
            <span className={cardAdPriceSubscriptionLabelClass}>{`${i18n.t('COMMON_VAT')} ${i18n.t(
              isBusinessTargetBuyer ? 'CARD_AD_NOT_INCLUDED' : 'CARD_AD_INCLUDED'
            )}`}</span>
          </div>
        </>
      )}
    </div>
  )
}

CardAdPriceSubscription.propTypes = {
  isExtended: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPreview: PropTypes.bool,
  vertical: PropTypes.bool,
  hasBothTargetBuyerOffers: PropTypes.bool,
  hasFinancedPrice: PropTypes.bool,
  hasFootNote: PropTypes.bool,
  monthlySubscriptionFee: PropTypes.number,
  priceRankIndicator: PropTypes.object,
  subscriptionPriceOption: PropTypes.object,
  subscriptionPriceOptions: PropTypes.array,
  targetBuyer: PropTypes.string
}
